import {Injectable} from '@angular/core';
import {PlanModelService} from './plan-model.service';
import {PLAN_LODGEMENT_STAGE, PLAN_LODGEMENT_STAGE_ACRONYM} from '../constants';
import { HttpClient } from '@angular/common/http';
import {catchError, concatMap, map, Observable, of, tap, throwError} from 'rxjs';
import {AppConfigService} from '../service/app-config.service';
import {AlertModalService} from '../component/modal/alert-modal/alert-modal.service';
import {saveAs} from '../service/file-handler.util';

export interface ValidationResponse {
    error?: boolean;
    errors?: string[];
}

@Injectable({
    providedIn: 'root'
})
export class ExportPlanService {
    title = 'Plan CSD Service';
    constructor(
        private planModelService: PlanModelService,
        private http: HttpClient,
        private appConfigService: AppConfigService,
        private alertModalService: AlertModalService
    ) {}

    generateCSD(): Observable<any> {
        const today = new Date();
        const dateString = this.addZero(today.getDate()) + this.addZero(today.getMonth() + 1) + today.getFullYear();
        const hours = this.addZero(today.getHours());
        const minutes = this.addZero(today.getMinutes());

        const fileName =
            this.planModelService.getModel().planType.smpId +
            this.planModelService.getModel().planNumber +
            '_v' +
            this.planModelService.getModel().versionNumber +
            '_' +
            PLAN_LODGEMENT_STAGE_ACRONYM[this.planModelService.getModel().planLodgementStage] +
            '_' +
            dateString +
            '_' +
            hours +
            minutes +
            '.csd';

        // Only save the plan before generating the CSD if the plan has not been lodged yet
        // Saving a lodged plan is invalid and will result in an error
        const saveObservable =
            this.planModelService.getModel().planLodgementStage === PLAN_LODGEMENT_STAGE.LODGED ? of(null) : this.planModelService.save();

        return saveObservable.pipe(
            concatMap(() => {
                return this.http.post(`${this.appConfigService.settings.apiGatewayUrl}/csd/generateCSD`, this.planModelService.getModel().id, {
                    responseType: 'arraybuffer'
                });
            }),
            tap(response => {
                let data = new Blob([response], {type: 'text/plain'});
                saveAs(data, fileName);
            }),
            catchError(error => {
                return throwError(() => {
                    const errMsg = 'An error occurred while generating CSD';
                    console.error(errMsg, error);
                    this.alertModalService.createAlertMessage(errMsg);
                    new Error(error);
                });
            })
        );
    }

    addZero(value: number): string {
        return value < 10 ? `0${value}` : `${value}`;
    }

    validateInput(fileType: string): Observable<any> {
        console.debug(this.title + ': validateInput');

        const url = this.appConfigService.settings.apiGatewayUrl + '/csd/validateInput';
        let isValidInput = true;
        return this.http.post(url, this.planModelService.getModel().id).pipe(
            map(data => {
                const validationResponse: ValidationResponse = data;
                console.debug(validationResponse);
                let errorLabelFields = [];

                if (validationResponse.error) {
                    isValidInput = false;
                    if (validationResponse.errors !== null && validationResponse.errors !== undefined && validationResponse.errors.length > 0) {
                        errorLabelFields.push(
                            fileType + ' file could not be exported as errors exist in the survey data. Please correct the errors and try again.'
                        );
                        validationResponse.errors.forEach(error => {
                            errorLabelFields.push(error);
                        });
                    }

                    this.alertModalService.createAlertMessage(errorLabelFields, 'Cancel', fileType + ' Export Error');
                    return isValidInput;
                }
                return isValidInput;
            }),
            catchError(error => {
                return throwError(() => {
                    console.error('Error validating the input data ' + error);
                    const errorMessage = 'System error while validating the input data';
                    this.alertModalService.createAlertMessage(errorMessage);
                    new Error(error);
                });
            })
        );
    }
}
